/*
  Rouqe editorial interactions. Vanilla, no dependencies, loaded with `defer`.
  Everything degrades to a fully readable static page if this file never runs.
*/
(() => {
  const root = document.documentElement;
  root.classList.add('rq-js');

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motionOn = () => root.classList.contains('rq-motion') && !reduceMotion.matches;
  const scrollBehavior = () => (reduceMotion.matches ? 'auto' : 'smooth');
  const hasIO = 'IntersectionObserver' in window;

  /* ---------- Reveal on scroll & in-view flags ---------- */

  // Mask reveals start fully clipped, and a fully clipped element never counts as intersecting,
  // so those are watched through their parent instead.
  const revealTargets = new Map();
  const revealObserver = hasIO
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            (revealTargets.get(entry.target) || [entry.target]).forEach((el) => el.classList.add('is-in'));
            revealTargets.delete(entry.target);
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.06 }
      )
    : null;

  function observeReveal(el) {
    const target = el.dataset.rqReveal === 'mask' && el.parentElement ? el.parentElement : el;
    if (target === el) {
      revealObserver.observe(el);
      return;
    }
    const pending = revealTargets.get(target) || [];
    pending.push(el);
    revealTargets.set(target, pending);
    revealObserver.observe(target);
  }

  const inviewObserver = hasIO
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => entry.target.classList.toggle('is-inview', entry.isIntersecting));
      })
    : null;

  function scan(scope) {
    scope.querySelectorAll('[data-rq-reveal]:not(.is-in)').forEach((el) => {
      if (revealObserver && motionOn()) observeReveal(el);
      else el.classList.add('is-in');
    });
    scope.querySelectorAll('[data-rq-inview]').forEach((el) => {
      if (inviewObserver) inviewObserver.observe(el);
      else el.classList.add('is-inview');
    });
  }

  scan(document);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));

  /* ---------- Brand statement: words light up with scroll ---------- */

  class RqStatement extends HTMLElement {
    connectedCallback() {
      this.text = this.querySelector('[data-rq-words]');
      if (!this.text || !hasIO || !motionOn()) return;

      this.classList.add('is-ready');
      this.update = this.update.bind(this);
      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(this.update);
      };
      this.observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.update();
        } else {
          window.removeEventListener('scroll', this.onScroll);
          this.update();
        }
      });
      this.observer.observe(this);
      this.update();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }

    update() {
      this.ticking = false;
      const rect = this.text.getBoundingClientRect();
      const vh = window.innerHeight;
      const start = vh * 0.85;
      const end = vh * 0.4;
      const progress = (start - rect.top) / (rect.height + (start - end));
      this.style.setProperty('--rq-p', Math.min(1, Math.max(0, progress)).toFixed(4));
    }
  }

  /* ---------- Scroll story: pinned image follows the active chapter ---------- */

  class RqStory extends HTMLElement {
    connectedCallback() {
      this.chapters = Array.from(this.querySelectorAll('[data-rq-chapter]'));
      this.slides = Array.from(this.querySelectorAll('[data-rq-slide]'));
      this.counter = this.querySelector('[data-rq-current]');
      this.index = 0;
      if (this.chapters.length < 2 || !hasIO) return;

      this.classList.add('is-ready');
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.activate(Number(entry.target.dataset.rqChapter));
          });
        },
        { rootMargin: '-45% 0px -45% 0px' }
      );
      this.chapters.forEach((chapter) => this.observer.observe(chapter));
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    activate(index) {
      if (index === this.index) return;
      this.index = index;
      this.chapters.forEach((chapter, i) => chapter.classList.toggle('is-active', i === index));
      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      if (this.counter) this.counter.textContent = String(index + 1).padStart(2, '0');
    }
  }

  /* ---------- Product carousel: tabs, arrows, progress ---------- */

  class RqRail extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('.rq-rail__panel'));
      this.onResize = () => this.panels.forEach((panel) => panel.rqUpdate && panel.rqUpdate());

      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (event) => {
          const last = this.tabs.length - 1;
          const keys = { ArrowRight: i === last ? 0 : i + 1, ArrowLeft: i === 0 ? last : i - 1, Home: 0, End: last };
          if (!(event.key in keys)) return;
          event.preventDefault();
          this.select(keys[event.key], true);
        });
      });

      this.panels.forEach((panel) => this.bindPanel(panel));
      window.addEventListener('resize', this.onResize, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
    }

    select(index, focus) {
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
        tab.tabIndex = i === index ? 0 : -1;
      });
      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });
      if (focus && this.tabs[index]) this.tabs[index].focus();
      const panel = this.panels[index];
      if (panel && panel.rqUpdate) panel.rqUpdate();
    }

    bindPanel(panel) {
      const track = panel.querySelector('[data-rq-track]');
      if (!track) return;
      const thumb = panel.querySelector('[data-rq-thumb]');
      const foot = panel.querySelector('.rq-rail__foot');
      const prev = panel.querySelector('[data-rq-prev]');
      const next = panel.querySelector('[data-rq-next]');
      let ticking = false;

      const update = () => {
        ticking = false;
        const max = track.scrollWidth - track.clientWidth;
        const visible = track.scrollWidth ? track.clientWidth / track.scrollWidth : 1;
        const offset = max > 0 ? (track.scrollLeft / max) * (1 - visible) : 0;
        if (thumb) {
          thumb.style.setProperty('--rq-w', `${(visible * 100).toFixed(2)}%`);
          thumb.style.setProperty('--rq-x', `${(offset * 100).toFixed(2)}%`);
        }
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max - 2;
        if (foot) foot.classList.toggle('is-static', max <= 2);
      };

      const step = (direction) => {
        const item = track.querySelector('.rq-rail__item');
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const width = item ? item.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
        const perView = Math.max(1, Math.floor(track.clientWidth / width) - 1);
        track.scrollBy({ left: direction * width * perView, behavior: scrollBehavior() });
      };

      track.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(update);
        },
        { passive: true }
      );
      if (prev) prev.addEventListener('click', () => step(-1));
      if (next) next.addEventListener('click', () => step(1));
      panel.rqUpdate = update;
      update();
    }
  }

  /* ---------- Shop the look: hotspots <-> product list ---------- */

  class RqLook extends HTMLElement {
    connectedCallback() {
      this.dots = Array.from(this.querySelectorAll('[data-rq-dot]'));
      this.items = Array.from(this.querySelectorAll('[data-rq-item]'));
      this.list = this.querySelector('.rq-look__list');

      this.dots.forEach((dot) => {
        const index = Number(dot.dataset.rqDot);
        dot.addEventListener('click', () => {
          this.activate(index);
          this.bringIntoView(index);
        });
        dot.addEventListener('mouseenter', () => this.activate(index));
      });
      this.items.forEach((item) => {
        const index = Number(item.dataset.rqItem);
        item.addEventListener('mouseenter', () => this.activate(index));
        item.addEventListener('focusin', () => this.activate(index));
      });
    }

    activate(index) {
      this.dots.forEach((dot) => dot.classList.toggle('is-active', Number(dot.dataset.rqDot) === index));
      this.items.forEach((item) => item.classList.toggle('is-active', Number(item.dataset.rqItem) === index));
    }

    bringIntoView(index) {
      const item = this.items.find((el) => Number(el.dataset.rqItem) === index);
      if (!item || !this.list) return;
      if (this.list.scrollWidth > this.list.clientWidth + 2) {
        const padding = parseFloat(getComputedStyle(this.list).paddingLeft) || 0;
        this.list.scrollTo({ left: item.offsetLeft - padding, behavior: scrollBehavior() });
      }
    }
  }

  /* ---------- Category index: preview image follows the cursor ---------- */

  class RqIndex extends HTMLElement {
    connectedCallback() {
      this.float = this.querySelector('.rq-cindex__float');
      this.list = this.querySelector('.rq-cindex__list');
      this.previews = Array.from(this.querySelectorAll('[data-rq-preview]'));
      if (!this.float || !this.list) return;
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

      this.pos = { x: 0, y: 0 };
      this.target = { x: 0, y: 0 };
      this.raf = null;

      this.list.addEventListener('pointerenter', (event) => {
        this.classList.add('is-hovering');
        this.setTarget(event, true);
        this.loop();
      });
      this.list.addEventListener('pointerleave', () => this.classList.remove('is-hovering'));
      this.list.addEventListener('pointermove', (event) => {
        this.setTarget(event);
        this.loop();
      });
      this.querySelectorAll('[data-rq-row]').forEach((link) => {
        const show = () => this.show(Number(link.dataset.rqRow));
        link.addEventListener('pointerenter', show);
        link.addEventListener('focus', show);
      });
    }

    disconnectedCallback() {
      if (this.raf) cancelAnimationFrame(this.raf);
    }

    setTarget(event, snap) {
      const rect = this.getBoundingClientRect();
      this.target.x = event.clientX - rect.left;
      this.target.y = event.clientY - rect.top;
      if (snap) {
        this.pos.x = this.target.x;
        this.pos.y = this.target.y;
      }
    }

    loop() {
      if (this.raf) return;
      const ease = reduceMotion.matches ? 1 : 0.16;
      const tick = () => {
        const dx = this.target.x - this.pos.x;
        const dy = this.target.y - this.pos.y;
        this.pos.x += dx * ease;
        this.pos.y += dy * ease;
        const tilt = reduceMotion.matches ? 0 : Math.max(-7, Math.min(7, dx * 0.05));
        this.float.style.setProperty('--mx', `${this.pos.x.toFixed(1)}px`);
        this.float.style.setProperty('--my', `${this.pos.y.toFixed(1)}px`);
        this.float.style.setProperty('--mr', `${tilt.toFixed(2)}deg`);
        this.raf = Math.abs(dx) + Math.abs(dy) > 0.4 ? requestAnimationFrame(tick) : null;
      };
      this.raf = requestAnimationFrame(tick);
    }

    show(index) {
      this.previews.forEach((preview, i) => preview.classList.toggle('is-active', i === index));
    }
  }

  const define = (name, element) => {
    if (!customElements.get(name)) customElements.define(name, element);
  };
  define('rq-statement', RqStatement);
  define('rq-story', RqStory);
  define('rq-rail', RqRail);
  define('rq-look', RqLook);
  define('rq-index', RqIndex);

  /* ---------- Theme editor: selecting a carousel tab block opens that tab ---------- */

  document.addEventListener('shopify:block:select', (event) => {
    const rail = event.target.closest && event.target.closest('rq-rail');
    if (!rail || !rail.tabs) return;
    const index = rail.tabs.indexOf(event.target);
    if (index > -1) rail.select(index);
  });
})();
